/**
 * Entity rendering: one `InstancedMesh` per entity kind per rigid sub-part
 * (KTD2).
 *
 * At this internal resolution the fragment cost is negligible and the binding
 * constraint is **draw calls and material state changes**, not triangles. Units
 * are hierarchically animated — hull, turret and tread band move
 * independently — so the batching unit is the sub-part, which keeps 400 units
 * at tens of draw calls rather than hundreds.
 *
 * Palette roles are baked into vertex colours per `(kind, owner)` pair at boot.
 * That trades a few hundred small geometries for using the stock Lambert
 * material with no custom shader, no per-instance colour attribute, and the
 * same lighting path the terrain already uses — which matters because the
 * quantizer is calibrated against that lighting.
 */

import {
  BufferAttribute,
  BufferGeometry,
  Color,
  InstancedMesh,
  Matrix4,
  MeshLambertMaterial,
  Quaternion,
  Scene,
  ShaderChunk,
  Vector3,
} from 'three'
import type { Palette } from '../forge/palette.js'
import {
  PART,
  SHADE,
  ORDNANCE_PARTS,
  TURRET_PARTS,
  mountPartFor,
  type PartKind,
} from '../forge/meshes.js'
import { recipeFor } from '../forge/recipes.js'
import type { RigidWalk } from '../forge/rigid-walk.js'
import { Entities, EF } from '../sim/entities.js'
import { defById, FACTION, ID, TAB } from '../sim/data/entities.js'
import { MAP_SIZE, MAX_ENTITIES } from '../core/config.js'

/** What the entity renderer needs from vision, as a shape rather than a class. */
interface VisionView {
  canSee(player: number, slot: number): boolean
  rememberedAt(player: number, cell: number): number
}
import type { TransformBuffer } from './interp.js'
import { detailSurfaces } from './surfaces.js'

const MAX_PER_KIND = 512

/** How high an aircraft flies above the ground under it, in cells. */
export const AIR_ALTITUDE = 1.5

/**
 * The least direct light a *unit* surface keeps, whichever way it faces.
 *
 * Ambient alone leaves a face turned from the sun at about a third of the lit
 * one, and on a small object most of the visible surface is exactly that face:
 * measured off `mid-battle.png`, the single most common colour in a green unit's
 * pixels is its own shaded flank, and it renders at 64 display luminance against
 * ground at 68. So the largest part of an army was, numerically, the value of the
 * dirt behind it, and the faction colour only existed on the narrow lit top.
 *
 * The reference frames do not shade a unit that far down — a tank's side face is
 * plainly the player's colour there, a step below its roof rather than a
 * silhouette. Flooring `N·L` on the entity material alone is the same mechanism
 * the ground already uses (`liftGroundShade`) and reaches nothing else: at 0.18 a
 * unit face turned fully away from the sun lands at about 0.55 of its lit top
 * instead of 0.32. Not further — a unit still has to read as a solid object, and
 * `sheet-helios` is where that is judged.
 *
 * A unit standing in a *cast* shadow is untouched, because the shadow term
 * multiplies the floored direct light rather than being floored by it. That is why
 * this bought entity/terrain contrast about a point and a half rather than the six
 * the shaded-flank arithmetic suggests: in a canyon frame much of an army is in
 * shadow, and lifting that too would be lifting the shadow itself.
 *
 * This is not the ambient lift that was tried and reverted. Ambient brightens the
 * *ground's* shaded faces at the same time, which is the contrast this exists to
 * protect; a floor inside `RE_Direct_Lambert` reaches only the material asked to
 * take it. Faces already above the floor are untouched, so the lit end stays
 * where the quantizer was calibrated, and a uniform scale of a palette colour is
 * still that colour.
 *
 * **Raised to 0.33, and this is what pays for the terrain's value structure.**
 * The entity/terrain contrast gate is `|unitMean − groundMean|` over one battle
 * frame, so for four rounds it held the *ground* hostage: every attempt to lift
 * the cliff faces and plateau tops off near-black measured as an army losing its
 * separation, and the palette notes ended up arguing that the reference's own
 * value structure could not be followed. The asymmetry was never real. The same
 * difference opens from the unit side, and the unit side is where the slack was —
 * at 0.18 a face turned from the sun kept 0.55 of its albedo, and on an object a
 * few dozen pixels across that face is most of what you see, so an army was
 * averaging well below its own body colour while the ground sat at its lit value.
 * At 0.33 that face keeps 0.65, so a green flank is plainly green rather than a
 * dark green, which is exactly what the reference frames show and exactly what a
 * jury asked for when it said to mix the pure team colour back in after shading.
 * Measured on the captures: an azure body's shaded flank went from 90 display
 * luminance to 113 and the slot means from 100.7 and 94.0 to 121 and 111.
 *
 * Still a floor and still local: lit faces are untouched, so a unit keeps a
 * top-to-flank step of about 1.4:1 — the ratio the reference reads at, and
 * `sheet-helios` confirms a unit still resolves as a solid object — and nothing
 * but this material sees it.
 *
 * The paragraph above is the one thing here that is now out of date: cast shadows
 * on units are *not* untouched any more. See `ENTITY_SHADOW`.
 *
 * **0.29 rather than 0.33, and only so that a vertical face can still be told
 * from the one next to it.** See `ENTITY_SIDE_TIER`: the tier multiplies before
 * this floor, so at 0.33 a sunward wall and a wall turned from the sun both
 * clamped to the same value and every box lost its corner. The four points cost
 * a shaded face under 3% of its display luminance, which is a fiftieth of what
 * the lift above bought it.
 */
const ENTITY_SHADE_FLOOR = 0.29

/**
 * How much direct light a *vertical* face of a unit or structure keeps,
 * relative to an up-facing one. The terrain's equivalent is `GROUND_SIDE_TIER`
 * in `render/index.ts`, and this is the same mechanism for the same reason.
 *
 * The sun stands at `N·L` 0.70 on a top face and 0.56 on the wall it lights, so
 * before this the two differed by 6% of display luminance once ambient was
 * added — measured on `sheet-helios`, a white slab's roof and its sunward side
 * were the same colour, and a stack of boxes with no step between its faces has
 * no blockiness left to read. The terrain solved this a while ago by tiering on
 * *facing* rather than on `N·L` alone, and the models never got the same
 * treatment.
 *
 * At 0.74 the four things a box is made of — top, sunward side, side, shaded
 * side — sit at 1.00, 0.87, 0.83 and 0.80 of each other in display luminance
 * where they used to sit at 1.00, 0.94, 0.89 and 0.83, so the step off the top
 * face is twice what it was. The floor comes down from 0.33 to 0.29 at the same
 * time, which is what keeps the two vertical faces separating from *each
 * other*: with the floor above the tiered value both walls clamp to the same
 * number and a box loses its corner.
 *
 * **0.74 and not the 0.68 this was first solved at**, which is the whole budget
 * this had. The critic's entity/terrain contrast metric is the mean luminance of
 * an army against the ground it stands on, and a unit is seen mostly on its
 * *sides* — so every point of tier comes straight off that metric. At 0.68 it
 * measured 0.551 against a bar of 0.55, which is not a margin. The step off the
 * top face is what the defect was about and it survives at 0.74; the last of the
 * spread is not worth an army that cannot be found.
 *
 * Like the ground's, this is a uniform scale of the direct term — the palette
 * colour under less light, so colour discipline is untouched, and the top tier
 * the lighting was calibrated against is left alone so nothing new clips.
 * `geometryNormal` is in view space and the camera is locked, so world up is
 * the second column of `viewMatrix`.
 */
const ENTITY_SIDE_TIER = 0.74

/**
 * How much of a cast shadow a *unit* keeps. The ground's equivalent is
 * `GROUND_SHADOW` in `render/index.ts`.
 *
 * The shade floor above only reaches the term before a shadow is sampled, and
 * measured off `mid-battle.png` that left most of the problem in place: the single
 * largest bucket of azure-body pixels in the frame is the one at 0.32 of its own
 * albedo, which is ambient alone — an army fighting in the shadow of the mesa it
 * is fighting over. A jury put it plainly: blue survives on bare sand and dies in
 * shadow. Two of the five asked for exactly this, to hold a shadowed tank at the
 * value and hue of a lit one.
 *
 * At 0.5 a unit in shadow keeps 0.62 of its albedo rather than 0.32 — still a
 * clear step down, so a unit driving under a cliff visibly enters the shade, but
 * not the near-silhouette it was. This is the entity material only: a unit still
 * casts a full-strength shadow onto the ground, so nothing about how it sits in
 * the world changes, and the one thing this genre cannot afford — an army you
 * cannot find — is what it buys.
 *
 * It is also two thirds of the contrast this round spent on the terrain. The
 * shade floor alone moved the slot means by two points, because the shadowed
 * pixels it cannot reach are most of an army in a canyon frame; this moved them by
 * twelve.
 */
const ENTITY_SHADOW = 0.5

const HALF_PI = Math.PI / 2
const UP = new Vector3(0, 1, 0)
const FORWARD = new Vector3(0, 0, 1)

/** How far a hovercraft floats, and how far it drifts either side of that. */
const HOVER_HEIGHT = 0.09
const HOVER_BOB = 0.045
/** Radians of lean. Small: past this it reads as damage rather than as motion. */
const HOVER_BANK = 0.11
/** A walking stride, and the jostle of a tracked hull over broken ground. */
const WALK_BOB = 0.05
const GROUND_JOSTLE = 0.022
const ONE = new Vector3(1, 1, 1)

/** Exaggerated silhouettes, kept narrow enough to distinguish adjacent units. */
const UNIT_SCALE = new Vector3(1.65, 1.65, 1.65)
const INFANTRY_SCALE = new Vector3(1.33, 1.33, 1.33)

const scaleCache = new Map<number, Vector3>()

/** Shared by model transforms and screen-space picking. */
export function scaleFor(kind: number): Vector3 {
  const cached = scaleCache.get(kind)
  if (cached) return cached
  const def = defById(kind)
  const scale = def?.tab === TAB.Infantry ? INFANTRY_SCALE : UNIT_SCALE
  scaleCache.set(kind, scale)
  return scale
}

/**
 * How a kind carries itself.
 *
 * The blocky direction lives or dies on movement. A cube standing perfectly
 * still reads as scenery however well it is shaded, and the cheapest way to
 * make one read as a machine is to let it breathe: a hovercraft that drifts and
 * banks, infantry that bob as they walk, a tank that settles on its suspension.
 * None of this touches simulation state, so none of it can affect the hash.
 */
const MOTION = {
  /** Treads or wheels. Settles when still, jostles over ground when moving. */
  Ground: 0,
  /** Never quite touches down: always drifting, and banks into a turn. */
  Hover: 1,
  /** Legs. Bobs in time with a stride. */
  Walk: 2,
  /** Structures and anything else that should be perfectly still. */
  Static: 3,
} as const

const motionCache = new Map<number, number>()

function motionFor(kind: number, structure: boolean, air: boolean): number {
  if (structure) return MOTION.Static
  const cached = motionCache.get(kind)
  if (cached !== undefined) return cached
  const def = defById(kind)
  let motion: number = MOTION.Ground
  if (air) motion = MOTION.Hover
  else if (def?.tab === TAB.Infantry || kind === ID.SiegeWalker || kind === ID.Colossus) motion = MOTION.Walk
  else if (def?.faction === FACTION.Ascendancy) motion = MOTION.Hover
  motionCache.set(kind, motion)
  return motion
}
/** A spent ordnance pylon: present in the batch, drawn at nothing. */
const ZERO = new Vector3(0, 0, 0)

/** Opposing stance and swing phases; at least one leg group stays planted. */
export function walkerStride(phase: number, side: number, moving: boolean): { forward: number; lift: number } {
  if (!moving) return { forward: 0, lift: 0 }
  return {
    forward: Math.sin(phase) * side * 0.22,
    lift: Math.max(0, Math.cos(phase) * side) * 0.12,
  }
}

interface KindBatch {
  parts: Array<{ kind: PartKind; mesh: InstancedMesh; walk?: RigidWalk }>
  copies: readonly (readonly number[])[]
  capacity: number
  rigged: boolean
  count: number
}

export class EntityRenderer {
  private readonly firedAt = new Float64Array(MAX_ENTITIES).fill(-100)

  recoil(slot: number, tick: number): void {
    this.firedAt[slot] = tick
  }
  private readonly batches = new Map<number, KindBatch>()
  private readonly material: MeshLambertMaterial
  private readonly scratchVec = new Vector3()
  private readonly scratchQuat = new Quaternion()
  private readonly scratchMatrix = new Matrix4()
  private readonly partMatrix = new Matrix4()
  private readonly copyMatrix = new Matrix4()
  private readonly bankQuat = new Quaternion()

  /** Reported by the profiler; draw calls are the binding constraint here. */
  drawCalls = 0
  triangles = 0

  constructor(
    private readonly scene: Scene,
    private readonly palette: Palette,
  ) {
    this.material = new MeshLambertMaterial({ vertexColors: true, flatShading: true })
    this.liftShade()
  }

  /**
   * Floor the direct term on the entity material, and stop a cast shadow taking
   * a unit down to ambient. See `ENTITY_SHADE_FLOOR` and `ENTITY_SHADOW`.
   *
   * Re-applied after `attachFog`, because `Fog.attach` *replaces*
   * `onBeforeCompile` rather than chaining onto it — applied only in the
   * constructor this patch would be silently dropped the moment fog arrived.
   * Applying it twice is harmless: the second pass runs the first as its
   * `previous`, and its own replace then finds no `#include` left to match.
   *
   * Both halves have to expand their `#include` by hand: `onBeforeCompile` runs
   * before Three resolves its chunks, so a replace against text inside one
   * silently matches nothing. The assertions are what keep a Three upgrade that
   * renames either term loud instead of quietly reverting this.
   */
  private liftShade(): void {
    const term = 'float dotNL = saturate( dot( geometryNormal, directLight.direction ) );'
    const pars = ShaderChunk.lights_lambert_pars_fragment
    if (!pars.includes(term)) throw new Error('three lambert direct term moved')
    const floored = pars.replace(
      term,
      `${term}
	float entityUp = dot( geometryNormal, viewMatrix[ 1 ].xyz );
	dotNL *= mix( ${ENTITY_SIDE_TIER}, 1.0, step( 0.5, entityUp ) );
	dotNL = max( dotNL, ${ENTITY_SHADE_FLOOR} );`,
    )
    const shadowTerm = 'directionalLightShadow.shadowIntensity'
    const begin = ShaderChunk.lights_fragment_begin
    if (!begin.includes(shadowTerm)) throw new Error(`three shadow term moved: ${shadowTerm}`)
    const softened = begin.replace(shadowTerm, `( ${shadowTerm} * ${ENTITY_SHADOW} )`)
    const previous = this.material.onBeforeCompile.bind(this.material)
    this.material.onBeforeCompile = (shader, renderer) => {
      previous(shader, renderer)
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <lights_lambert_pars_fragment>', floored)
        .replace('#include <lights_fragment_begin>', softened)
    }
    this.material.customProgramCacheKey = () => 'entity-shade'
    detailSurfaces(this.material)
  }

  /** Instanced meshes for a kind and owner, created on first sight. */
  private batchFor(kind: number, owner: number): KindBatch | null {
    const key = kind * 16 + (owner < 0 ? 15 : owner)
    const existing = this.batches.get(key)
    if (existing) return existing

    const recipe = recipeFor(kind)
    if (!recipe) return null

    const copies = recipe.copies ?? [[0, 0, 0]]
    const batch: KindBatch = { parts: [], count: 0, copies, capacity: MAX_PER_KIND * copies.length,
      rigged: recipe.parts.some(part => part.walk) }
    for (const part of recipe.parts) {
      const mesh = new InstancedMesh(
        bakeShades(part.geometry, this.palette, owner),
        this.material,
        batch.capacity,
      )
      // Every sub-part casts, which is what makes an infantry squad throw three
      // shadows rather than one rectangle, and puts a gun barrel's shadow on
      // the ground beside its tank.
      mesh.castShadow = true
      mesh.receiveShadow = true
      mesh.frustumCulled = false
      mesh.count = 0
      this.scene.add(mesh)
      batch.parts.push({ kind: part.kind, mesh, walk: part.walk })
    }
    this.batches.set(key, batch)
    return batch
  }

  /** Write this frame's instance transforms. */
  /**
   * Whether the local player may see this entity at all.
   *
   * Dimming is not enough for units: an enemy tank standing in explored ground
   * you are not currently watching must be *gone*, which is the Red Alert rule
   * and the one that makes scouting mean something. Structures are different —
   * a base you have seen stays on the map, because a building does not move and
   * pretending you forgot where it was is not fog, it is amnesia.
   */
  /** The same visibility rule, for anything drawn alongside a unit. */
  canSeeSlot(slot: number): boolean {
    return this.entitiesRef ? this.canDraw(this.entitiesRef, slot) : true
  }

  private entitiesRef: Entities | null = null

  private canDraw(entities: Entities, slot: number): boolean {
    const vision = this.vision
    if (!vision || this.localSlot < 0) return true
    if (entities.owner[slot] === this.localSlot) return true
    if (vision.canSee(this.localSlot, slot)) return true
    if (!entities.has(slot, EF.Structure)) return false
    const cx = entities.x[slot] >> 16
    const cy = entities.y[slot] >> 16
    return vision.rememberedAt(this.localSlot, cy * MAP_SIZE + cx) !== 0
  }

  /** Fog source. Set by the renderer; absent in the harness, which sees all. */
  vision: VisionView | null = null
  localSlot = -1

  /** Darken units by the same fog the terrain uses. */
  attachFog(fog: { attach(material: MeshLambertMaterial): void }): void {
    fog.attach(this.material)
    this.liftShade()
  }

  /** Tear down every batch so the next frame rebuilds them. */
  private dropBatches(): void {
    for (const batch of this.batches.values()) {
      for (const part of batch.parts) {
        this.scene.remove(part.mesh)
        part.mesh.geometry.dispose()
        part.mesh.dispose()
      }
    }
    this.batches.clear()
  }

  /**
   * Ground height under a world position. Set by the renderer from the terrain
   * it built; without it a unit on a plateau is drawn buried in the rock it is
   * standing on.
   */
  groundAt: (x: number, z: number) => number = () => 0

  sync(entities: Entities, transforms: TransformBuffer, alpha: number, tick: number): void {
    for (const batch of this.batches.values()) batch.count = 0

    this.entitiesRef = entities
    const limit = entities.limit
    for (let slot = 0; slot < limit; slot++) {
      if (!entities.isAlive(slot)) continue
      if (!this.canDraw(entities, slot)) continue
      const batch = this.batchFor(entities.kind[slot], entities.owner[slot])
      if (!batch || batch.count + batch.copies.length > batch.capacity) continue

      const x = transforms.x(slot, alpha)
      const z = transforms.y(slot, alpha)
      // Aircraft fly. The altitude is generous — a third of a cell reads as
      // "slightly off the ground" from a fixed camera, and the one thing the
      // player must never wonder about an air unit is whether it is an air
      // unit.
      const ground =
        this.groundAt(x, z) + (entities.has(slot, EF.Air) ? AIR_ALTITUDE : 0)
      const structure = entities.has(slot, EF.Structure)
      // A structure never rotates with a heading; a unit does. Turrets track
      // independently of both, which is the whole reason for sub-parts.
      //
      // The quarter turn is the conversion between the two conventions in play.
      // Headings are `atan2(dy, dx)`, so zero means +X; recipes are modelled
      // nose-along-+Z, because that is the axis a hull is longest on. Rotating
      // by `-heading` alone leaves every unit ninety degrees off its direction
      // of travel — invisible on a tank, whose turret does the pointing, and
      // unmissable on an aircraft shaped like a cross.
      const bodyYaw = structure ? 0 : HALF_PI - transforms.heading(slot, alpha)
      const turretYaw = HALF_PI - transforms.turret(slot, alpha)

      // Idle and travelling motion. The phase is offset by the slot so a column
      // of identical units ripples instead of pulsing in lockstep, which is the
      // difference between an army and a chorus line.
      const moving = entities.vx[slot] !== 0 || entities.vy[slot] !== 0
      const motion = motionFor(entities.kind[slot], structure, entities.has(slot, EF.Air))
      const scale = structure ? ONE : scaleFor(entities.kind[slot])
      const phase = (tick + alpha + slot * 13) * 0.09
      let lift = 0
      let bank = 0
      switch (motion) {
        case MOTION.Hover:
          // Always airborne, and leaning into whatever it is doing.
          lift = HOVER_HEIGHT + Math.sin(phase) * HOVER_BOB
          bank = Math.sin(phase * 0.7) * (moving ? HOVER_BANK : HOVER_BANK * 0.35)
          break
        case MOTION.Walk:
          // A stride, not a float: the bob is one-sided, because a leg pushes
          // off the ground and does not sink into it.
          if (moving && !batch.rigged) lift = Math.abs(Math.sin(phase * 3.1)) * WALK_BOB
          break
        case MOTION.Ground:
          if (moving) lift = Math.sin(phase * 2.3) * GROUND_JOSTLE
          break
        default:
          break
      }

      // A spent pylon is drawn at zero scale rather than skipped, because the
      // instance index has to stay aligned across every part of the batch.
      const ammo = entities.ammo[slot]
      // Which ring-mount fitting this transport shows, decided by the
      // passenger. Non-transports carry none of the mount parts, so the
      // lookup only matters where it applies.
      const mount = mountPartFor(
        entities.passenger[slot] !== 0 ? defById(entities.passenger[slot]) : undefined,
      )
      for (let copy = 0; copy < batch.copies.length; copy++) {
        for (const part of batch.parts) {
          const round = ORDNANCE_PARTS.indexOf(part.kind as (typeof ORDNANCE_PARTS)[number])
          const isMountPart =
            part.kind === PART.MountStub ||
            part.kind === PART.MountGun ||
            part.kind === PART.MountLong ||
            part.kind === PART.MountCrane
          // Hidden the same way a spent pylon is: scaled to nothing rather than
          // skipped, because the instance index has to stay aligned across every
          // part of the batch.
          const hidden =
            (round >= 0 && ammo <= round) ||
            (part.kind === PART.Works && (!entities.has(slot, EF.DugIn) || copy > 0)) ||
            (isMountPart && part.kind !== mount)
          this.scratchVec.set(x, ground + lift, z)
          if (!part.walk && (part.kind === PART.LegA || part.kind === PART.LegB)) {
            const stride = walkerStride(phase * 3.1, part.kind === PART.LegA ? 1 : -1, moving)
            this.scratchVec.set(
              x + Math.sin(bodyYaw) * stride.forward,
              ground + stride.lift,
              z + Math.cos(bodyYaw) * stride.forward,
            )
          }
          if (part.kind === PART.Tread && !structure && moving) {
            // Tread bob keyed to the tick and the slot, so a column of units does
            // not pulse in lockstep. Render-side only; it never touches
            // simulation state and so cannot affect the hash.
            this.scratchVec.y = ground + lift + Math.sin((tick + slot * 7) * 0.6) * 0.012
          }
          this.scratchQuat.setFromAxisAngle(UP, TURRET_PARTS.includes(part.kind) ? turretYaw : bodyYaw)
          if (TURRET_PARTS.includes(part.kind)) {
            const kick = Math.max(0, 1 - (tick + alpha - this.firedAt[slot]) / 7) * 0.075
            this.scratchVec.x -= Math.sin(turretYaw) * kick
            this.scratchVec.z -= Math.cos(turretYaw) * kick
          }
          // Bank is applied after the yaw, so a hovercraft leans about its own
          // long axis rather than about the world's.
          if (bank !== 0) {
            this.bankQuat.setFromAxisAngle(FORWARD, bank)
            this.scratchQuat.multiply(this.bankQuat)
          }
          this.scratchMatrix.compose(this.scratchVec, this.scratchQuat, hidden ? ZERO : scale)
          if (part.kind !== PART.Works) {
            const offset = batch.copies[copy]
            this.copyMatrix.makeTranslation(offset[0], offset[1], offset[2])
            this.scratchMatrix.multiply(this.copyMatrix)
          }
          if (part.walk && moving) {
            const walkTime = (tick + alpha) / 30 + slot * 0.13 + copy * 0.21
            this.scratchMatrix.multiply(part.walk.sample(walkTime, this.partMatrix))
          }
          part.mesh.setMatrixAt(batch.count, this.scratchMatrix)
        }
        batch.count++
      }
    }

    this.drawCalls = 0
    this.triangles = 0
    for (const batch of this.batches.values()) {
      for (const part of batch.parts) {
        part.mesh.count = batch.count
        part.mesh.instanceMatrix.needsUpdate = true
        if (batch.count === 0) continue
        this.drawCalls++
        const index = part.mesh.geometry.getIndex()
        this.triangles += ((index?.count ?? 0) / 3) * batch.count
      }
    }
  }

  dispose(): void {
    this.dropBatches()
    this.material.dispose()
  }
}

/**
 * Resolve a recipe's `shade` roles into concrete vertex colours for one owner
 * slot. Ownership is a **per-slot ramp**, never a faction colour (R34).
 *
 * Exported because the icon forge needs exactly the same mapping — an icon
 * whose colours were derived separately would drift from the unit it depicts.
 */
export function bakeShades(source: BufferGeometry, palette: Palette, owner: number): BufferGeometry {
  const geometry = source.clone()
  const shade = source.getAttribute('shade')
  const count = source.getAttribute('position').count
  const colors = new Float32Array(count * 3)

  const ramp = palette.ownerRamps[owner < 0 ? 0 : owner % palette.ownerRamps.length]
  const neutral = palette.neutralRamp
  const trim = palette.accentRamps.trim ?? palette.neutralRamp

  const pick = (role: number): [number, number, number] => {
    switch (role) {
      case SHADE.Owner:
        return palette.rgb(ramp.indices[Math.min(1, ramp.indices.length - 1)])
      case SHADE.OwnerDark:
        return palette.rgb(ramp.indices[0])
      case SHADE.OwnerLight:
        return palette.rgb(ramp.indices[ramp.indices.length - 1])
      case SHADE.Metal:
        // The light step of a **three-value chassis ramp**: light deck, mid
        // body, dark undercarriage, at roughly 0.66 / 0.42 / 0.20 lightness.
        //
        // It used to be one step higher, at 0.86, which is near enough to white
        // that a structure's alloy mass and the sky-bright top of it were the
        // same colour — the "white slab" a critic counted on `sheet-helios`.
        // A light neutral still has to be plainly lighter than the mid body
        // beside it and plainly darker than nothing at all, and 0.66 is where
        // the reference frames put a lit chassis panel.
        return palette.rgb(neutral.indices[3] ?? neutral.indices[0])
      case SHADE.MetalMid:
        // The mid step, and the one the reference spends most of a chassis on:
        // hull sides, engine covers, glacis plates. See `SHADE.MetalMid`.
        return palette.rgb(neutral.indices[2] ?? neutral.indices[0])
      case SHADE.MetalDark:
        // The dark step — treads, barrels, running gear, every recessed panel.
        //
        // This was two steps up the ramp, at 0.42, on the grounds that the
        // darkest neutral reads as a hole punched through the model. It does,
        // but 0.42 is the *mid* value, so raising it here deleted the dark band
        // entirely and left a unit with no neutral mass darker than the ground
        // it stands on. 0.20 is the reference's contact band: dark enough to
        // read as under-structure, three steps clear of the near-black at the
        // bottom of the ramp that caused the trouble.
        return palette.rgb(neutral.indices[1] ?? neutral.indices[0])
      case SHADE.Glass:
        return palette.rgb(palette.accentRamps.helios.indices[0])
      case SHADE.Light:
        return palette.rgb(neutral.indices[4])
      default:
        // Warm and bright, and deliberately not the ore ramp.
        //
        // This is the trim role: fittings, barrels, markings, lamps. It used to
        // resolve to the top step of the *regolith* ramp — the colour of the
        // ore lying on the ground — so every accent on every unit was within a
        // few degrees of the terrain it stood on. Pointing it at a bright
        // warning yellow instead went too far the other way and made the whole
        // roster look like roadworks. Warm mid-tone brass sits between the two.
        return palette.rgb(trim.indices[trim.indices.length - 1])
    }
  }

  const scratch = new Color()
  const cache = new Map<number, [number, number, number]>()
  for (let i = 0; i < count; i++) {
    const role = shade ? shade.getX(i) : SHADE.Owner
    let rgb = cache.get(role)
    if (!rgb) {
      rgb = pick(role)
      cache.set(role, rgb)
    }
    // Vertex colours live in Three's linear working space while the palette is
    // sRGB. Converting here is what keeps a baked colour landing on the same
    // palette entry the quantizer would have chosen for it.
    scratch.setRGB(rgb[0] / 255, rgb[1] / 255, rgb[2] / 255)
    colors[i * 3] = scratch.r
    colors[i * 3 + 1] = scratch.g
    colors[i * 3 + 2] = scratch.b
  }

  geometry.setAttribute('color', new BufferAttribute(colors, 3))
  geometry.deleteAttribute('shade')
  return geometry
}
